<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('orders.purchasedOrdersTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
            <span v-if="showButtonLabels">{{ $t('orders.filtersToggleLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendToExport">
            <i class="material-icons">explicit</i>
            <span v-if="showButtonLabels">{{ $t('datatable.exportButton') }}</span>
          </a>
          <a v-if="$_has_perm('purchase_generate_qrcode')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openGenerateQrCodeModal()">
            <i class="fa fa-qrcode"></i>
            <span v-if="showButtonLabels">{{ $t('datatable.qrCodeShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('send_to_warehouse')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openSendToWarehouseModal()">
            <i class="material-icons" style="color:#006b8d">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendToWarehouseButton') }}</span>
          </a>
          <a v-if="$_has_perm('send_to_shipment')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendAllToIntShipment()">
            <i class="material-icons" style="color:#006b8d">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendAllToShipmentButton') }}</span>
          </a>
          <a v-if="$_has_perm('send_to_shipment_by_qrcode')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="openSendToShipmentModal()">
            <i class="material-icons" style="color:#006b8d">send</i>
            <span v-if="showButtonLabels">{{ $t('datatable.sendToShipmentButton') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ summaryTotalOrders }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ summaryTotalAmount.toFixed(2) }}</span>
              <span v-if="qrCodeValues.length > 0" class="order-stat-note">({{ qrCodeValues.length }} selected)</span>
              <span v-else-if="!filterMade" class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">payment</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.pendingPaymentsLabel') }}</span>
              <span class="order-stat-value">{{ pendingPaymentsCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="row">
        <preDatatableComponent
          ref="preDatatable"
          :permissions="{
            generate_qr_code: 'purchase_generate_qrcode',
            send_to_warehouse: 'send_to_warehouse',
            send_to_shipment: 'send_to_shipment',
            send_to_shipment_by_qrcode: 'send_to_shipment_by_qrcode',
          }"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :openSendToWarehouseModal="openSendToWarehouseModal"
          :sendAllToIntShipment="sendAllToIntShipment"
          :openSendToShipmentModal="openSendToShipmentModal"
          :openGenerateQrCodeModal="openGenerateQrCodeModal"
          :sendToExport="sendToExport"
          :tableData="tableData"
          :resetGetResults="resetGetResults"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :resetGetResults2="resetGetResults2"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :storageKey="'cols_purchasedOrder_v3'"
          :defaultHideCol="defaultHideCol"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :warehouseFilterItems="warehouses"
          :warehouseFilterChange="handleWarehouseFilter"
          :platformFilterItems="platformFilterItems"
          :platformFilterChange="handlePlatformFilter"
          @branch-edit="branchEdit"
        ></preDatatableComponent>
        <div class="col-xs-12 pt-0">
          <!-- Warehouse Passed checkbox filter - hidden for later use, keep tableData.wh_dt_passed wiring intact
          <v-layout
            row
            wrap
          >
            <v-flex
              xs5
              sm4
              md5
              lg3
              class="pl-3"
            >
              <v-checkbox
                hide-details
                :label="$t('shipOnly.warehousePassedLabel')"
                @change="getResults()"
                v-model="tableData.wh_dt_passed"
                true-value="1"
                false-value="0"
              />
            </v-flex>
          </v-layout>
          -->
          <div class="table-responsive oc-table-shell">
          <datatable
            v-if="!isMobile"
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="(r, index) in apiResults"
                :key="r.order_id"
                :style="qrCodeValues.find(x => x[0] == r.order_id) ? 'background-color: #FFF9C4' : (r.is_ship_only == '1' ? 'background-color: #A5D6A7' : getRowStyle(r)) "
                class="table-tr"
              >
                <td>
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="[
                      r.order_id,
                      r.full_name,
                      r.phone_number,
                      r.user_id,
                      r.theOrderID,
                      r.is_ship_only,
                      r.product_name,
                      null,
                      null,
                      null,
                      r.total_amount,
                    ]"
                    v-model="qrCodeValues"
                  />
                </td>
                <td class="oc-cell hover-container">
                  <div class="oc-order-number">
                    <i v-if="r.qrcode" class="fa fa-qrcode" aria-hidden="true"></i> #{{ r.theOrderID }}
                  </div>
                  <div class="oc-order-sub" v-if="r.qrcode"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</div>
                  <div class="oc-order-sub" v-if="r.track_number">{{ $t('columns.trackNumber') }}: {{ r.track_number }}</div>
                  <div class="oc-order-sub" v-if="r.last_order_invoice">{{ $t('columns.invoiceNumber') }}: {{ r.last_order_invoice }}</div>
                  <div class="oc-order-sub"><view-order-users :orderData="r" /></div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                  <customer-device-icon :orderSource="r.order_source" />
                  <span class="customer-name-stack">
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break: break-word; white-space: pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>{{ r.full_name }}</span>
                    <span v-if="r.customer_id_fk" class="oc-customer-id">ID: {{ r.customer_id_fk }}</span>
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                </td>
                <td class="oc-cell">
                  <div class="oc-item-name">{{ r.product_name ? r.product_name.substr(0, 35) + (r.product_name.length > 35 ? '...' : '') : '—' }}</div>
                  <div class="oc-item-sub" v-if="r.brand">{{ $t('columns.brand') }}: <strong>{{ r.brand }}</strong></div>
                  <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" class="oc-item-link">
                    <i class="material-icons">link</i> {{ $t('columns.url') }}
                  </a>
                </td>
                <td class="oc-cell oc-product-cell">
                  <img
                    v-if="r.image"
                    :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                    class="oc-thumb"
                    @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                  />
                  <span v-else class="oc-thumb-placeholder">—</span>
                </td>
                <td class="oc-cell">
                  <span class="oc-status-chip" style="background:#84B179; color:#1A3D15;">{{ $_status_label(r.status) }}</span>
                  <div class="oc-location-line" v-if="r.country">
                    <i class="material-icons oc-location-icon">place</i>{{ r.country }}<span v-if="r.transportation_type_obj"> · {{ r.transportation_type_obj.name.replace('Transportation', '') }}</span>
                  </div>
                  <div class="oc-item-sub" v-if="r.purchase_note">{{ $t('columns.purchaseNote') }}: {{ r.purchase_note }}</div>
                  <div class="oc-item-sub" v-if="r.note">{{ $t('columns.note') }}: {{ r.note }}</div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.price') }}:</span> {{ r.item_price ? '$' + r.item_price : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.shipping') }}:</span> {{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.total') }}:</span> {{ r.total_amount ? '$' + r.total_amount : '—' }}</div>
                    <div class="combined-cell-divider"></div>
                    <div><span class="combined-cell-label">{{ $t('columns.qty') }}:</span> {{ r.quantity || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.size') }}:</span> {{ r.size || '—' }}</div>
                  </div>
                </td>
                <td class="oc-cell hover-container" style="min-width: 150px">
                  <view-order-dates :orderData="r" stack-time />
                  <span
                    v-if="r.payment_status"
                    class="oc-payment-chip"
                    :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color }"
                  >{{ paymentStatusLabel(r.payment_status) }}</span>
                </td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      v-if="$_has_perm('view_order_detail')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.viewDetails')"
                      @click="initDetail('modal_showApp', r.order_id)"
                    >
                      <i class="material-icons" style="color:#01579B">aspect_ratio</i>
                    </a>
                    <a
                      v-if="$_has_perm('purchase_cancel_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.cancelThisOrder')"
                      @click="openCancelingOrderModal(r.order_id, r.total_amount)"
                    ><i class="material-icons" style="color:#c62828">cancel</i></a>
                    <a
                      v-if="$_has_perm('copy_purchased_order_to_new_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.copyToNewOrderTooltip')"
                      @click="copyOrderToNewOrder(r)"
                    ><i class="material-icons" style="color:#6a1b9a">content_copy</i></a>
                    <a
                      v-if="$_has_perm('refund_purchased_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.refundThisOrderTooltip')"
                      @click="openRefundOrderModal(r.order_id, r.total_amount)"
                    >
                      <i class="material-icons" style="color:#C62828">block</i>
                    </a>
                    <a
                      v-if="$_has_perm('add_track_number')"
                      class="oc-action-btn"
                      :tooltip="$t('customer.addTrackNumberTooltip')"
                      @click="openAddTrackNumberModal(r.order_id, r.track_number)"
                    >
                      <i class="material-icons" style="color:rgb(4, 137, 151)">add_box</i>
                    </a>
                    <a
                      v-if="$_has_perm('add_order_number')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.addOrderNumberTooltip')"
                      @click="openAddBarcodeModal(r.order_id, r.barcode)"
                    >
                      <i class="material-icons" style="color:#00796B">add_circle</i>
                    </a>
                    <a
                      v-if="$_has_perm('add_warehouse_date')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.addWarehouseDateTooltip')"
                      @click="openAddWarehouseDateModal(r.order_id, r.warehouse_date)"
                    >
                      <i class="material-icons" style="color:#5579fB">add</i>
                    </a>
                    <a
                      v-if="$_has_perm('add_partial_refund')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.partialRefundTooltip')"
                      @click="openPartialRefundOrderModal(r.order_id, r.total_amount)"
                    >
                      <i class="fa fa-registered" style="color: #FF5722"></i>
                    </a>
                    <a
                      v-if="$_has_perm('purchase_generate_qrcode')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.generateQrCodeTooltip')"
                      @click="openQrCodeForRow(r)"
                    >
                      <i class="fa fa-qrcode" style="color:#7B1FA2"></i>
                    </a>
                    <a
                      v-if="$_has_perm('add_order_case')"
                      class="oc-action-btn"
                      @click="openCreateOrderCaseModal(r)"
                      :tooltip="$t('orders.createCaseTooltip')"
                    >
                      <i class="fa fa-folder-open" style="color: #555"></i>
                    </a>
                    <a
                      v-if="$_has_perm('process_order')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.processOrderTooltip')"
                      @click="openProcessOrderModal(r)"
                    >
                      <i class="material-icons" style="color:#424242">settings</i>
                    </a>
                    <span v-if="$_has_perm('edit_charged_order') && r.confirm_date" class="oc-action-btn">
                      <edit-order-charge
                        :r="r"
                        :getResults="getResults"
                      ></edit-order-charge>
                    </span>
                    <span v-if="$_has_perm('reassign_order_customer') && r.confirm_date" class="oc-action-btn">
                      <reassign-order-customer
                        :r="r"
                        :getResults="getResults"
                      ></reassign-order-customer>
                    </span>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          </div>

          <div v-if="isMobile" class="moc-container">
            <div
              v-for="(r, index) in apiResults"
              :key="r.order_id"
              class="moc-card"
              :style="qrCodeValues.find(x => x[0] == r.order_id) ? 'border-left-color:#FBC02D; background:#FFF9C4;' : (r.is_ship_only == '1' ? 'border-left-color:#43A047; background:#A5D6A7;' : getRowStyle(r))"
            >
              <div class="moc-header">
                <input
                  :id="'cb' + index"
                  type="checkbox"
                  :value="[r.order_id, r.full_name, r.phone_number, r.user_id, r.theOrderID, r.is_ship_only, r.product_name, null, null, null, r.total_amount]"
                  v-model="qrCodeValues"
                />
                <span class="moc-item-code">
                  <i v-if="r.qrcode" class="fa fa-qrcode" aria-hidden="true"></i> {{ r.theOrderID }}
                  <span v-if="r.track_number" style="font-size:0.8rem; color:#555;" class="rtl-ml-6">| {{ r.track_number }}</span>
                </span>
                <span
                  class="moc-status-badge"
                  style="background:#84B179; color:#1A3D15;"
                >{{ $_status_label(r.status) }}</span>
              </div>
              <div class="moc-body">
                <!-- QR -->
                <div class="moc-row" v-if="r.qrcode">
                  <span class="moc-label">{{ $t('columns.qr') }}</span>
                  <span class="moc-value"><i class="fa fa-qrcode" aria-hidden="true"></i> {{ r.qrcode }}</span>
                </div>
                <!-- Invoice # | Customer ID -->
                <div class="moc-row--2col">
                  <div><span class="moc-label">{{ $t('recordInvoicePayment.invoiceHashLabel') }}</span><span class="moc-value">{{ r.last_order_invoice || '—' }}</span></div>
                  <div><span class="moc-label">{{ $t('columns.customerId') }}</span><span class="moc-value">{{ r.customer_id_fk || '—' }}</span></div>
                </div>
                <!-- Phone Number -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.phone') }}</span>
                  <span class="moc-value">{{ r.phone_number || '—' }}</span>
                </div>
                <!-- User -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('common.user') }}</span>
                  <span class="moc-value"><view-order-users :orderData="r" /></span>
                </div>
                <!-- Customer -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.customer') }}</span>
                  <span class="moc-value">
                    <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                    <customer-device-icon :orderSource="r.order_source" />
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break:break-word; white-space:pre-line;">{{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}</span>
                    <span v-else>{{ r.full_name }}</span>
                    <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                  </span>
                </div>
                <!-- Item Name -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.itemName') }}</span>
                  <span class="moc-value">{{ r.product_name ? r.product_name.substr(0, 60) + (r.product_name.length > 60 ? '...' : '') : '—' }}</span>
                </div>
                <!-- Brand -->
                <div class="moc-row" v-if="r.brand">
                  <span class="moc-label">{{ $t('columns.brand') }}</span>
                  <span class="moc-value">{{ r.brand }}</span>
                </div>
                <!-- URL -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.url') }}</span>
                  <span class="moc-value">
                    <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" style="color:#01579B;">
                      <i class="material-icons" style="font-size:20px; vertical-align:middle;">link</i>
                    </a>
                    <span v-else style="color:#bbb;">—</span>
                  </span>
                </div>
                <!-- Image -->
                <div class="moc-row" v-if="r.image">
                  <span class="moc-label">{{ $t('columns.image') }}</span>
                  <span class="moc-value">
                    <img
                      :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                      style="width:80px; height:80px; object-fit:cover; border-radius:4px; cursor:pointer;"
                      @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                    />
                  </span>
                </div>
                <!-- Date & Time -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.dateTime') }}</span>
                  <span class="moc-value"><view-order-dates :orderData="r" /></span>
                </div>
                <!-- Warehouse -->
                <div class="moc-row" v-if="r.country">
                  <span class="moc-label">{{ $t('columns.warehouse') }}</span>
                  <span class="moc-value">{{ r.country }}</span>
                </div>
                <!-- Type -->
                <div class="moc-row" v-if="r.transportation_type_obj">
                  <span class="moc-label">{{ $t('columns.type') }}</span>
                  <span class="moc-value">{{ r.transportation_type_obj.name.replace('Transportation', '') }}</span>
                </div>
                <!-- Price | Shipping | Total -->
                <div class="moc-row--3col">
                  <div>
                    <span class="moc-label">{{ $t('columns.price') }}</span>
                    <span class="moc-value">{{ r.item_price ? '$' + r.item_price : '—' }}</span>
                  </div>
                  <div>
                    <span class="moc-label">{{ $t('columns.shipping') }}</span>
                    <span class="moc-value">{{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</span>
                  </div>
                  <div>
                    <span class="moc-label">{{ $t('columns.total') }}</span>
                    <span class="moc-value">{{ r.total_amount ? '$' + r.total_amount : '—' }}</span>
                  </div>
                </div>
                <!-- Qty | Size -->
                <div class="moc-row--2col">
                  <div>
                    <span class="moc-label">{{ $t('columns.qty') }}</span>
                    <span class="moc-value">{{ r.quantity || '—' }}</span>
                  </div>
                  <div>
                    <span class="moc-label">{{ $t('columns.size') }}</span>
                    <span class="moc-value">{{ r.size || '—' }}</span>
                  </div>
                </div>
                <!-- Purchase Note -->
                <div class="moc-row" v-if="r.purchase_note">
                  <span class="moc-label">{{ $t('columns.purchaseNote') }}</span>
                  <span class="moc-value">{{ r.purchase_note }}</span>
                </div>
                <!-- Order Note -->
                <div class="moc-row" v-if="r.note">
                  <span class="moc-label">{{ $t('shipOnly.orderNoteColumn') }}</span>
                  <span class="moc-value">{{ r.note }}</span>
                </div>
                <!-- Item Price in Warehouse Currency -->
                <div class="moc-row moc-currency-block" v-if="localItemPrice(r)">
                  <span class="moc-label">{{ localItemPrice(r).label }}</span>
                  <span class="moc-value">
                    <span class="moc-currency-chip">{{ localItemPrice(r).display }}</span>
                  </span>
                </div>
                <!-- Warehouse Date (hidden) -->
                <!-- <div class="moc-row--2col">
                  <div><span class="moc-label">{{ $t('shipOnly.whDateColumn') }}</span><span class="moc-value">{{ r.warehouse_date || '—' }}</span></div>
                  <div><span class="moc-label">{{ $t('columns.orderHash') }}</span><span class="moc-value">{{ r.barcode || '—' }}</span></div>
                </div> -->
              </div>
              <div class="moc-actions">
                <a
                  v-if="$_has_perm('view_order_detail')"
                  style="color:#01579B;"
                  :tooltip="$t('orders.viewDetails')"
                  @click="initDetail('modal_showApp', r.order_id)"
                ><i class="material-icons">aspect_ratio</i></a>
                <a
                  v-if="$_has_perm('purchase_cancel_order')"
                  style="color:#c62828;"
                  :tooltip="$t('orders.cancelThisOrder')"
                  @click="openCancelingOrderModal(r.order_id, r.total_amount)"
                ><i class="material-icons">cancel</i></a>
                <a
                  v-if="$_has_perm('copy_purchased_order_to_new_order')"
                  style="color:#6A1B9A;"
                  :tooltip="$t('orders.copyToNewOrderTooltip')"
                  @click="copyOrderToNewOrder(r)"
                ><i class="material-icons">content_copy</i></a>
                <a
                  v-if="$_has_perm('refund_purchased_order')"
                  style="color:#C62828;"
                  :tooltip="$t('orders.refundThisOrderTooltip')"
                  @click="openRefundOrderModal(r.order_id, r.total_amount)"
                ><i class="material-icons">block</i></a>
                <a
                  v-if="$_has_perm('add_track_number')"
                  style="color:rgb(4,137,151);"
                  :tooltip="$t('customer.addTrackNumberTooltip')"
                  @click="openAddTrackNumberModal(r.order_id, r.track_number)"
                ><i class="material-icons">add_box</i></a>
                <a
                  v-if="$_has_perm('add_order_number')"
                  style="color:#00796B;"
                  :tooltip="$t('orders.addOrderNumberTooltip')"
                  @click="openAddBarcodeModal(r.order_id, r.barcode)"
                ><i class="material-icons">add_circle</i></a>
                <a
                  v-if="$_has_perm('add_warehouse_date')"
                  style="color:#5579fB;"
                  :tooltip="$t('orders.addWarehouseDateTooltip')"
                  @click="openAddWarehouseDateModal(r.order_id, r.warehouse_date)"
                ><i class="material-icons">add</i></a>
                <a
                  v-if="$_has_perm('add_partial_refund')"
                  style="color:#FF5722;"
                  :tooltip="$t('orders.partialRefundTooltip')"
                  @click="openPartialRefundOrderModal(r.order_id, r.total_amount)"
                ><i class="fa fa-registered" style="font-size:1.7em"></i></a>
                <a
                  v-if="$_has_perm('purchase_generate_qrcode')"
                  style="color:#7B1FA2;"
                  :tooltip="$t('orders.generateQrCodeTooltip')"
                  @click="openQrCodeForRow(r)"
                ><i class="fa fa-qrcode" style="font-size:1.4em"></i></a>
                <a
                  v-if="$_has_perm('add_order_case')"
                  @click="openCreateOrderCaseModal(r)"
                  style="color:#555;"
                  :tooltip="$t('orders.createCaseTooltip')"
                ><i class="fa fa-folder-open" style="font-size:1.6rem"></i></a>
                <a
                  v-if="$_has_perm('process_order')"
                  style="color:#424242;"
                  :tooltip="$t('orders.processOrderTooltip')"
                  @click="openProcessOrderModal(r)"
                ><i class="material-icons">settings</i></a>
                <edit-order-charge
                  v-if="$_has_perm('edit_charged_order') && r.confirm_date"
                  :r="r"
                  :getResults="getResults"
                ></edit-order-charge>
                <reassign-order-customer
                  v-if="$_has_perm('reassign_order_customer') && r.confirm_date"
                  :r="r"
                  :getResults="getResults"
                ></reassign-order-customer>
              </div>
            </div>
            <div v-if="alertValue" style="text-align:center; padding:16px;">
              <v-alert type="error" :value="alertValue">{{ $t('common.noResultsFound') }}</v-alert>
            </div>
          </div>

          <view-order-details
            :detialData="detialData"
            :closeModal="closeModal"
          ></view-order-details>

          <create-qr-code
            :openGenerateQrCodeModal="openGenerateQrCodeModal"
            :generateQrCodeModal="generateQrCodeModal"
            :closeGenerateQrCodeModal="closeGenerateQrCodeModal"
            :qrCodeValues="qrCodeValues"
            :generateQrCode="generateQrCode"
            :getResults="getResults"
            :advanceToIntShipment="true"
          ></create-qr-code>

          <send-to-warehouse
            :sendToWarehouseModal="sendToWarehouseModal"
            :closeSendToWarehouseModal="closeSendToWarehouseModal"
            :sendToWarehouseData="sendToWarehouseData"
            :sendToWarehouse="sendToWarehouse"
          ></send-to-warehouse>

          <send-to-shipment
            :sendToShipmentModal="sendToShipmentModal"
            :closeSendToShipmentModal="closeSendToShipmentModal"
            :sendToShipmentData="sendToShipmentData"
            :sendToShipment="sendToShipment"
          ></send-to-shipment>

          <process-the-order
            v-if="currentProcessOrder"
            ref="processComp"
            :processOrder="processOrder"
            :r="currentProcessOrder"
            :checkAlert="checkAlert"
            :updateData="updateData"
            :calculateTotalAmount="calculateTotalAmount"
            :loading="loading"
            :getResults="getResults"
            :clearProcessOrderData="clearProcessOrderData"
            :theQuantity="theQuantity"
            :itemTypes="itemTypes"
            :warehouses="warehouses"
            :reasons="reasons"
          ></process-the-order>

          <refunding-order
            :refundOrderModal="refundOrderModal"
            :refundOrder="refundOrder"
            :refundData="refundData"
            :checkAlert="checkAlert"
            :closeRefundOrderModal="closeRefundOrderModal"
            :loading="loading"
            :theOrderTotalAmount="theOrderTotalAmount"
            :reasons="reasons"
          ></refunding-order>

          <add-barcode
            :addBarcodeModal="addBarcodeModal"
            :addBarcode="addBarcode"
            :addBarcodeData="addBarcodeData"
            :checkAlert="checkAlert"
            :closeAddBarcodeModal="closeAddBarcodeModal"
          ></add-barcode>

          <add-track-number
            :addTrackNumberModal="addTrackNumberModal"
            :addTrackNumber="addTrackNumber"
            :addTrackNumberData="addTrackNumberData"
            :checkAlert="checkAlert"
            :closeAddTrackNumberModal="closeAddTrackNumberModal"
          ></add-track-number>

          <take-order-image
            :order_id_fk="oID"
            :takeOrderImageDialog="takeOrderImageDialog"
          />

          <add-warehouse-date
            :addWarehouseDateModal="addWarehouseDateModal"
            :warehouseDateData="warehouseDateData"
            :closeWarehouseDateModal="closeWarehouseDateModal"
            :submitWarehouseDate="submitWarehouseDate"
          />

          <take-photo
            :takePhotoModal="takePhotoModal"
            :orderData="orderData"
            @close="takePhotoModal = false"
          />

          <partial-refunding-order
            :partialRefundOrderModal="partialRefundOrderModal"
            :partialRefundOrder="partialRefundOrder"
            :partialRefundData="partialRefundData"
            :theOrderTotalAmount="theOrderTotalAmount"
            :checkAlert="checkAlert"
            :closePartialRefundOrderModal="closePartialRefundOrderModal"
            :loading="loading"
          ></partial-refunding-order>

          <add-edit-order-case
            :addEditOrderCaseModal="addEditOrderCaseModal"
            :formData="formData"
            :isUpdate="isUpdate"
            @close="addEditOrderCaseModal = false"
          />

          <admin-cancel-order
            :closeCancelingOrderModal="closeCancelingOrderModal"
            :cancelOrderModal="cancelOrderModal"
            :cancelAlert="cancelAlert"
            :cancelData="cancelData"
            :cancelingOrder="cancelingOrder"
            :reasons="reasons"
            :willAutoRefund="true"
            :refundAmount="cancelRefundAmount"
          ></admin-cancel-order>

          <!-- Image Lightbox -->
          <v-dialog v-model="lightboxOpen" max-width="600" @input="val => { if (!val) lightboxOpen = false }">
            <v-card style="background:#000; text-align:center; padding:8px;">
              <img v-if="lightboxImage" :src="lightboxImage" style="max-width:100%; max-height:80vh; object-fit:contain; border-radius:4px;" />
              <div style="margin-top:8px;"><v-btn flat dark @click="lightboxOpen = false">{{ $t('common.close') }}</v-btn></div>
            </v-card>
          </v-dialog>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from './Pagination.vue'
import preDatatableComponent from './preDatatableComponent.vue'
import DatatableVue from './Datatable.vue'
import viewOrderDetails from './viewOrderDetails.vue'
import axios from 'axios'
import swal from 'sweetalert'
import createQrCode from './createQrCode.vue'
import sendToWarehouse from './sendToWarehouse.vue'
import sendToShipment from './sendToShipment.vue'
import refundingOrder from './refundingOrder.vue'
import addBarcode from './addBarcode.vue'
import addTrackNumber from './addTrackNumber.vue'
import takeOrderImage from './takeOrderImage'
import addWarehouseDate from './addWarehouseDate'
import takePhoto from './takePhoto.vue'
import partialRefundingOrder from './partialRefundingOrder.vue'
import addEditOrderCase from './orderCase/addEditOrderCase.vue'
import adminCancelOrder from './adminCancelOrder'
import editOrderCharge from './editOrderCharge.vue'
import reassignOrderCustomer from './reassignOrderCustomer.vue'
import processTheOrder from './processTheOrder.vue'
import statusHelpers from '../mixins/statusHelpers'
import toolbarButtonDisplay from '../mixins/toolbarButtonDisplay'

export default {
  mixins: [statusHelpers, toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    'view-order-details': viewOrderDetails,
    'create-qr-code': createQrCode,
    'send-to-warehouse': sendToWarehouse,
    'send-to-shipment': sendToShipment,
    'refunding-order': refundingOrder,
    'add-barcode': addBarcode,
    'add-track-number': addTrackNumber,
    'admin-cancel-order': adminCancelOrder,
    'edit-order-charge': editOrderCharge,
    'reassign-order-customer': reassignOrderCustomer,
    'process-the-order': processTheOrder,
    takeOrderImage,
    addWarehouseDate,
    takePhoto,
    partialRefundingOrder,
    addEditOrderCase
  },
  created() {
    this.getLoggedInUser()
  },
  mounted() {
    this.$_fetch_statuses()
    this.getWarehouses()
    this.getReasons()
    this.getItemType()
    this.perPageFix()

    if (screen.width <= 600) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }
  },
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    let sortOrders = {}
    // Grouped columns (redesign, mirrors allOrder.vue's pattern) -- was a flat ~25-column
    // list; each grouped cell stacks several related fields (QR / track # / invoice # /
    // processed-by user fold into Order Info; purchase & order notes into Status & Location).
    // Per-column hide now only makes sense at the group level, so defaultHideCol is emptied
    // and storageKey bumped so stale hidden-column selections don't misapply to new positions.
    let columns = [
      { label: '', name: '', text: this.$t('columns.checkBoxColumn'), value: '1' },
      { label: this.$t('columns.orderInfo'), text: this.$t('columns.orderInfo'), value: '2', sortable: true, searchable: true, name: 'theOrderID' },
      { label: this.$t('columns.customer'), text: this.$t('columns.customer'), value: '3', sortable: true, searchable: true, name: 'full_name' },
      { label: this.$t('columns.itemDetails'), text: this.$t('columns.itemDetails'), value: '4', sortable: true, searchable: true, name: 'product_name' },
      { label: this.$t('columns.product'), text: this.$t('columns.product'), value: '5', sortable: false, searchable: false, name: 'image' },
      { label: this.$t('columns.statusLocation'), text: this.$t('columns.statusLocation'), value: '6', sortable: true, searchable: true, name: 'status' },
      { label: this.$t('columns.pricing'), text: this.$t('columns.pricing'), value: '7', sortable: false, searchable: false, name: 'price_all' },
      { label: this.$t('columns.datePayment'), text: this.$t('columns.datePayment'), value: '8', sortable: true, searchable: false, name: 'purchase_date' },
      { label: this.$t('columns.actions'), text: this.$t('columns.actions'), value: '9', sortable: false, searchable: false, name: 'actions' }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      isMobile: false,
      loading: false,
      warehouses: [],
      reasons: [],
      lightboxImage: null,
      lightboxOpen: false,
      url: 'api/purchased_order',
      sendToWarehouseUrl: 'api/warehouse_delivering_order',
      generateQrCodeUrl: 'api/generate_qrcode',
      advanceToIntShipmentUrl: 'api/advance_to_int_shipment',
      advanceToIntShipmentByQrcodeUrl: 'api/advance_to_int_shipment_by_qrcode',
      orderDetailUrl: 'api/show_order_detail',
      refundOrderUrl: 'api/refunding_order',
      addBarcodeUrl: 'api/add_barcode',
      addTrackNumberUrl: 'api/add_track_number',
      processOrderUrl: 'api/orders',
      qrCodeValues: [],
      platformFilterItems: [
        { text: this.$t('common.mobileSource'), value: 'mobile' },
        { text: this.$t('common.webSource'), value: 'web' },
        { text: this.$t('common.adminSource'), value: 'admin' }
      ],
      loggedInUserData: {},
      addWarehouseDateModal: false,
      warehouseDateData: {},
      theCancelOrderID: '',
      cancelOrderModal: false,
      cancelAlert: false,
      cancelRefundAmount: null,
      cancelData: {
        cancel_reason: ''
      },
      sendToWarehouseData: {
        qrcode: '',
      },
      sendToShipmentData: {
        qrcode: '',
      },
      alertValue: false,
      checkAlert: false,
      filterMade: false,
      totalResult: 0,
      resultsVersion: 0,
      generateQrCodeModal: false,
      sendToWarehouseModal: false,
      sendToShipmentModal: false,
      refundOrderModal: false,
      queryParameter: true,
      the_order_id: '',
      btnsave: true,
      addBarcodeModal: false,
      addTrackNumberModal: false,
      the_order_id2: '',
      the_order_id3: '',
      oID: null,
      orderData: {},
      takePhotoModal: false,
      takeOrderImageDialog: false,
      formData: {},
      isUpdate: false,
      addEditOrderCaseModal: false,
      addBarcodeData: {
        barcode: '',
      },
      addTrackNumberData: {
        track_number: '',
      },
      updateData: {
        item_price: null,
        the_tl_price: null,
        the_aed_price: null,
        the_cny_price: null,
        the_gbp_price: null,
        the_eur_price: null,
        tax: null,
        national_tax: null,
        item_weight: null,
        internal_shipping_handling: null,
        delivery_price: null,
        kreenex_fee: null,
        quantity_charge: null,
        insurance: null,
        total_amount: null,
        from_date: null,
        to_date: null,
        description: '',
        tmp_shipping: null,
        item_weight_lb: null,
        price_weight: null,
        send_sms: false,
        discount: null,
        item_type: '',
        transportation_type: null,
        is_repackage: false,
        is_hold: false,
        other_service: '',
        local_delivery: 0,
        process_note: '',
        customer_process_note: '',
        repackage_price: null,
        hold_price: null,
        other_service_price: null,
        customer_id_fk: null,
        product_name: null,
        product_url: null,
        quantity: null,
        color: null,
        size: null,
        note: null,
        brand: null
      },
      // Process Order (single instance pattern, mirrors allOrder.vue)
      currentProcessOrder: null,
      theQuantity: { quantity: '' },
      itemTypeResult: [],
      itemTypes: [],
      getItemTypeUrl: 'api/get-item-type',
      refundData: {
        refund_reason: '',
        money_amount: '',
        refund_note: '',
        send_sms: false,
      },
      detialData: {
        the_tl_price: '',
        order_id: '',
        bank_name: '',
        discount: '',
        full_name: '',
        email: '',
        phone_number: '',
        c_o_d: '',
        product_url: '',
        product_name: '',
        description: '',
        size: '',
        type: '',
        status: '',
        color: '',
        quantity: '',
        quantity_charge: '',
        coupon: '',
        qrcode: '',
        barcode: '',
        delivery_type: '',
        item_price: '',
        tax: '',
        item_weight: '',
        internal_shipping_handling: '',
        kreenex_fee: '',
        delivery_price: '',
        insurance: '',
        total_amount: '',
        from_date: '',
        to_date: '',
        confirm_date: '',
        refund_reason: '',
        country: '',
        order_date: '',
        track_number: '',
        item_type: '',
        note: '',
      },
      prevSearchQuery: '',
      apiResults: [],
      detailResults: [],
      theOrderTotalAmount: '',
      columns: columns,
      // Grouped cells can't be hidden field-by-field any more -- same tradeoff allOrder.vue made.
      defaultHideCol: [],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_purchasedOrder') === 'true',
      sortKey: 'updated_at',
      sortOrders: sortOrders,
      tableData: {
        per_page: 10,
        search: '',
        column: 'orders.updated_at',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        wh_dt_passed: 0,
        branches_id: '',
        warehouse_id_fk: '',
        platform: ''
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
      partialRefundOrderModal: false,
      partialRefundData: {}
    }
  },

  computed: {
    summaryTotalOrders() {
      if (this.qrCodeValues.length > 0) return this.qrCodeValues.length
      return this.filterMade
        ? this.apiResults.length
        : (this.pagination.total || this.apiResults.length)
    },
    summaryTotalAmount() {
      return this.apiResults.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0)
    },
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count -- hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.purchase_date || r.order_date || r.created_at
        return d && d.substr(0, 10) === today
      }).length
    },
    // Same current-page-only caveat as ordersTodayCount above.
    pendingPaymentsCount() {
      return this.apiResults.filter(r => r.payment_status && r.payment_status !== 'paid').length
    }
  },

  methods: {
    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_purchasedOrder', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    paymentStatusColor(status, editCount) {
      if (status === 'not_paid' && !Number(editCount)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }
      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#F5F5F5', color: '#616161' }
    },

    paymentStatusLabel(status) {
      if (!status) return ''
      return status.replace(/_/g, ' ')
    },

    sendToExport() {
      let searchValue = this.tableData.search !== '' ? this.tableData.search : '0'
      let column = this.tableData.column !== '' ? this.tableData.column : '0'
      let sortDir = this.tableData.sortDir !== '' ? this.tableData.sortDir : '0'
      let searchedOneCol = this.tableData.searchOneCol !== '' ? this.tableData.searchOneCol : '0'
      let searchedOneVal = this.tableData.searchOneVal !== '' ? this.tableData.searchOneVal : '0'
      let fromDate = this.tableData.from_date !== '' ? this.tableData.from_date : '0'
      let toDate = this.tableData.to_date !== '' ? this.tableData.to_date : '0'
      let whDtPassed = this.tableData.wh_dt_passed !== '' ? this.tableData.wh_dt_passed : '0'
      let loggedInUserData = JSON.parse(localStorage.getItem('user_data'))
      let warehouses = loggedInUserData.warehouse_id_fk
      let perPage = this.tableData.per_page == null ? 10 : this.tableData.per_page
      if (
        searchValue !== '0' ||
        searchedOneCol !== '0' ||
        searchedOneVal !== '0' ||
        toDate !== '0' ||
        fromDate !== '0'
      ) {
        perPage = '0'
      }

      let hostName = window.location.hostname
      let protocol = window.location.protocol + '//'
      let fullHostName = protocol + hostName

      window.location.replace(
        fullHostName +
        '/api/purchased-order-export?searchValue=' +
        searchValue +
        '&column=' +
        column +
        '&sortDir=' +
        sortDir +
        '&searchedOneCol=' +
        searchedOneCol +
        '&searchedOneVal=' +
        searchedOneVal +
        '&from_date=' +
        fromDate +
        '&to_date=' +
        toDate +
        '&warehouses=' +
        warehouses +
        '&per_page=' +
        perPage +
        '&wh_dt_passed=' +
        whDtPassed
      )
    },

    openQrCodeForRow(r) {
      // Mirrors checkAll()'s array shape so createQrCode.vue's qr[10] (label price)
      // resolves to total_amount instead of undefined — a truncated array here was why
      // the printed price was always $0.00.
      this.qrCodeValues = [[
        r.order_id,
        r.full_name,
        r.phone_number,
        r.user_id,
        r.theOrderID,
        r.is_ship_only,
        r.product_name,
        null,
        null,
        null,
        r.total_amount
      ]]
      this.generateQrCodeModal = true
    },

    openCreateOrderCaseModal(order) {
      this.formData = {
        order_id_fk: order.order_id,
        order: order,
        note: null
      }
      this.isUpdate = false
      this.addEditOrderCaseModal = true
    },

    openPartialRefundOrderModal(orderId, totalAmount) {
      let token = localStorage.getItem('access_token')
      axios.get(`api/order/${orderId}/partial-refunds`, { headers: { Authorization: 'Bearer ' + token } }).then(res => {
        this.partialRefundData = {
          order_id_fk: orderId,
          partial_refunds: res.data.data.partial_refunds
        }
        this.theOrderTotalAmount = totalAmount
        this.partialRefundOrderModal = true
        this.the_order_id = orderId
      })
    },

    closePartialRefundOrderModal() {
      this.partialRefundOrderModal = false
      this.checkAlert = false
      this.partialClear()
    },

    partialClear() {
      this.partialRefundData = {}
      this.theOrderTotalAmount = ''
      this.$validator.reset()
    },

    partialRefundOrder() {
      let token = localStorage.getItem('access_token')
      this.loading = true
      axios.post('api/partial-refund', this.partialRefundData, {
        headers: { Authorization: 'Bearer ' + token }
      }).then((response) => {
        this.getResults()
        this.checkAlert = true
        this.loading = false
        setTimeout(() => {
          this.clear()
          this.closePartialRefundOrderModal()
        }, 1500)
      }).catch(error => {
        swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        this.getResults()
        this.loading = false
      })
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    openAddWarehouseDateModal(orderId, warehouseDate) {
      this.addWarehouseDateModal = true
      this.warehouseDateData = {
        order_id: orderId,
        warehouse_date: warehouseDate
      }
    },

    closeWarehouseDateModal() {
      this.addWarehouseDateModal = false
      this.warehouseDateData = {}
    },

    submitWarehouseDate() {
      let token = localStorage.getItem('access_token')

      axios
        .post('/api/change-warehouse-date', this.warehouseDateData, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then(() => {
          swal(this.$t('common.success'), this.$t('shipOnly.warehouseDateChangedSuccessfully'), 'success')
          this.closeWarehouseDateModal()
          this.getResults()
        })
        .catch((error) => {
          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    getRowStyle(data) {
      let today = new Date()
      let warehouseDate = new Date(data.warehouse_date)
      let next8Days = new Date(warehouseDate.getTime())
      next8Days.setDate(next8Days.getDate() + 8)

      if (next8Days < today) return 'background-color: #FFAB91'

      return ''
    },

    openTakeOrderImageModal(orderId) {
      this.oID = orderId
      this.takeOrderImageDialog = true
    },

    showHideColumn(prevColVal, currentColVal) {
      let a = []
      let diff = []

      if (currentColVal.length < prevColVal.length) {
        for (let i = 0; i < prevColVal.length; i++) {
          a[prevColVal[i]] = true
        }

        for (let i = 0; i < currentColVal.length; i++) {
          if (a[currentColVal[i]]) {
            delete a[currentColVal[i]]
          } else {
            a[currentColVal[i]] = true
          }
        }

        for (let k in a) {
          diff.push(k)
        }

        for (let c = 0; c < diff.length; c++) {
          $('td:nth-child(' + diff[c] + '),th:nth-child(' + diff[c] + ')').toggle()
        }
      } else {
        for (let i = 0; i < currentColVal.length; i++) {
          let visible = $(
            'td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')'
          ).is(':visible')
          if (visible) {
            $(
              'td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')'
            ).toggle()
          }
        }
      }
    },

    resetGetResults2() {
      this.resetGetResults()
    },

    getLoggedInUser() {
      this.loggedInUserData = JSON.parse(localStorage.getItem('user_data'))
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk action, affecting rows the user never intended
      // to touch in the current (filtered) view.
      this.qrCodeValues = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          if (theDatatable) { theDatatable.classList.remove('slide-table-out') }
          setTimeout(function () {
            thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.queryParameter = false
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'updated_at'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'updated_at'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.wh_dt_passed = '0'
      this.tableData.warehouse_id_fk = ''
      this.tableData.platform = ''
      this.getResults()
    },

    openGenerateQrCodeModal() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true,
        })
      } else {
        this.generateQrCodeModal = true
      }
    },

    closeGenerateQrCodeModal() {
      this.generateQrCodeModal = false
      this.qrCodeValues = []
      this.getResults()
    },

    generateQrCode() {
      this.closeGenerateQrCodeModal()
    },

    openSendToWarehouseModal() {
      this.sendToWarehouseModal = true
    },

    closeSendToWarehouseModal() {
      this.sendToWarehouseModal = false
      this.sendToWarehouseData.qrcode = ''
      this.$validator.reset()
    },

    sendToWarehouse() {
      let token = localStorage.getItem('access_token')

      axios
        .put(this.sendToWarehouseUrl, this.sendToWarehouseData, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((response) => {
          this.sendToWarehouseData.qrcode = ''
          this.orderData = response.data.message.order
          this.takePhotoModal = true
          this.getResults()
          swal({
            title: this.$t('common.done'),
            text: '',
            icon: 'success',
            timer: 500,
            buttons: false
          })
          if (response.data.message.process_note && response.data.message.process_note.length > 0) {
            swal(this.$t('common.warning'), response.data.message.process_note)
          }
        })
        .catch((error) => {
          this.sendToWarehouseData.qrcode = ''

          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    openSendToShipmentModal() {
      this.sendToShipmentModal = true
    },

    closeSendToShipmentModal() {
      this.sendToShipmentModal = false
      this.sendToShipmentData.qrcode = ''
      this.$validator.reset()
    },

    // Purchased -> Int Shipment shortcut, single order by QR code: same qrcode-entry pattern as
    // sendToWarehouse, but skips straight to Int Shipment (advance_to_int_shipment_by_qrcode
    // auto-fills the Warehouse Delivered / Pending Shipment dates on the backend).
    sendToShipment() {
      let token = localStorage.getItem('access_token')

      axios
        .put(this.advanceToIntShipmentByQrcodeUrl, this.sendToShipmentData, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then(() => {
          this.sendToShipmentData.qrcode = ''
          this.getResults()
          swal({
            title: this.$t('common.done'),
            text: '',
            icon: 'success',
            timer: 1000,
            buttons: false
          })
        })
        .catch((error) => {
          this.sendToShipmentData.qrcode = ''
          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    // Purchased -> Int Shipment shortcut: generates QR codes for the checked orders and
    // advances them straight to Int Shipment. advance_to_int_shipment auto-fills the
    // Warehouse Delivered / Pending Shipment dates for whichever of those steps the order
    // hasn't passed through yet, so this works from any pre-shipment status.
    sendAllToIntShipment() {
      if (this.qrCodeValues.length < 1) {
        swal({
          text: this.$t('orders.pleaseAtLeastSelectOneOrder'),
          icon: 'warning',
          dangerMode: true
        })
        return
      }

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.confirmGenerateQrSendIntShipment'),
        icon: 'warning',
        buttons: true
      }).then((confirmed) => {
        if (!confirmed) return

        let token = localStorage.getItem('access_token')
        let orderIds = this.qrCodeValues.map((q) => q[0])

        axios
          .put(this.generateQrCodeUrl, this.qrCodeValues, {
            headers: { Authorization: 'Bearer ' + token }
          })
          .then(() => axios.put(this.advanceToIntShipmentUrl, { orders_id: orderIds }, {
            headers: { Authorization: 'Bearer ' + token }
          }))
          .then(() => {
            this.qrCodeValues = []
            this.getResults()
            swal({
              title: this.$t('common.done'),
              text: '',
              icon: 'success',
              timer: 1500,
              buttons: false
            })
          })
          .catch((error) => {
            const msg = (error.response && error.response.data && error.response.data.message)
              ? error.response.data.message
              : this.$t('orders.failedToSendOrdersToIntShipment')
            swal(this.$t('common.error'), msg, 'error')
            this.getResults()
          })
      })
    },

    openCancelingOrderModal(orderId, totalAmount) {
      this.cancelOrderModal = true
      this.theCancelOrderID = orderId
      this.cancelRefundAmount = totalAmount || null
    },

    closeCancelingOrderModal() {
      this.cancelOrderModal = false
      this.cancelAlert = false
      this.cancelRefundAmount = null
      this.cancelData.cancel_reason = ''
    },

    cancelingOrder() {
      let token = localStorage.getItem('access_token')
      axios
        .put('api/canceling_order_by_admin/' + this.theCancelOrderID, this.cancelData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(() => {
          this.getResults()
          this.cancelAlert = true
          setTimeout(() => {
            this.closeCancelingOrderModal()
          }, 1500)
        }).catch(error => {
          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    copyOrderToNewOrder(r) {
      swal({
        title: this.$t('orders.copyToNewOrderConfirmTitle'),
        text: this.$t('orders.copyToNewOrderConfirmMessage', { id: r.theOrderID }),
        icon: 'warning',
        buttons: true,
        dangerMode: true
      }).then((confirmed) => {
        if (confirmed) {
          let token = localStorage.getItem('access_token')
          axios
            .post('api/orders/' + r.order_id + '/copy_to_new_order', {}, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then((response) => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: response.data.message,
                icon: 'success',
                timer: 2500,
                buttons: false
              })
            })
            .catch((error) => {
              swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
            })
        }
      })
    },

    openRefundOrderModal(orderId, totalAmount) {
      let token = localStorage.getItem('access_token')
      axios.get(`api/order/${orderId}/partial-refunds`, { headers: { Authorization: 'Bearer ' + token } }).then(res => {
        this.refundData = {
          partial_refunds: res.data.data.partial_refunds,
          send_sms: false,
          money_amount: totalAmount,
          refund_note: 'full refund'
        }
        this.the_order_id = orderId
        this.theOrderTotalAmount = totalAmount
        this.refundOrderModal = true
      })
    },

    closeRefundOrderModal() {
      this.refundOrderModal = false
      this.checkAlert = false
      this.clear()
    },

    clear() {
      this.refundData.refund_reason = ''
      this.refundData.money_amount = ''
      this.refundData.refund_note = ''
      this.theOrderTotalAmount = ''
      this.$validator.reset()
    },

    refundOrder() {
      let token = localStorage.getItem('access_token')
      this.loading = true
      axios
        .put(this.refundOrderUrl + '/' + this.the_order_id, this.refundData, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((response) => {
          this.getResults()
          this.checkAlert = true
          this.loading = false
          this.clear()
          let self = this
          setTimeout(() => {
            self.closeRefundOrderModal()
          }, 1500)
        })
        .catch((error) => {
          if (error.response) {
            swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
          }
        })
    },

    openAddBarcodeModal(id, barcode) {
      this.addBarcodeModal = true
      this.the_order_id2 = id
      this.addBarcodeData.barcode = barcode
    },

    closeAddBarcodeModal() {
      this.addBarcodeModal = false
      this.addBarcodeData.barcode = ''
      this.checkAlert = false
    },

    addBarcode() {
      let token = localStorage.getItem('access_token')

      axios
        .put(this.addBarcodeUrl + '/' + this.the_order_id2, this.addBarcodeData, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then(() => {
          this.getResults()
          this.checkAlert = true
          setTimeout(() => {
            this.closeAddBarcodeModal()
          }, 1500)
        })
        .catch((error) => {
          this.addBarcodeData.barcode = ''
          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    openAddTrackNumberModal(id, trackNumber) {
      this.addTrackNumberModal = true
      this.the_order_id3 = id
      this.addTrackNumberData.track_number = trackNumber
    },

    closeAddTrackNumberModal() {
      this.addTrackNumberModal = false
      this.addTrackNumberData.track_number = ''
      this.checkAlert = false
    },

    addTrackNumber() {
      let token = localStorage.getItem('access_token')

      axios
        .put(this.addTrackNumberUrl + '/' + this.the_order_id3, this.addTrackNumberData, {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then(() => {
          this.getResults()
          this.checkAlert = true
          setTimeout(() => {
            this.closeAddTrackNumberModal()
          }, 1500)
        })
        .catch((error) => {
          this.addTrackNumberData.track_number = ''
          swal(this.$t('common.error'), `${error.response.data.message}`, 'error')
        })
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    initDetail(modal, id) {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.orderDetailUrl + '/' + id, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          this.detailResults = data.data
          this.detialData = this.detailResults[0]
        })
      this.modal(modal)
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() != '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true,
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    },

    checkAll() {
      this.qrCodeValues = this.apiResults.map(r => [
        r.order_id,
        r.full_name,
        r.phone_number,
        r.user_id,
        r.theOrderID,
        r.is_ship_only,
        r.product_name,
        null,
        null,
        null,
        r.total_amount
      ])
    },

    unCheckAll() {
      this.qrCodeValues = []
    },

    getWarehouses() {
      let token = localStorage.getItem('access_token')
      this.warehouses = []
      axios.get('api/warehouses', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          response.data.data.forEach((w) => {
            this.warehouses.push({ text: w.country, value: w.warehouse_id, transportation_types: w.transportation_types, currency: w.currency, estimate_days: w.estimate_days })
          })
        })
    },

    getItemType() {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.getItemTypeUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.itemTypeResult = response.data.data
          this.itemTypeResult.forEach((c) => {
            this.itemTypes.push({
              text: c.type_name,
              value: c.type_name,
              shippingPrice: c.type_int_shipping_price
            })
          })
        })
    },

    // Process Order modal (single instance pattern, mirrors allOrder.vue)
    openProcessOrderModal(r) {
      const warehouseObj = this.warehouses.find(w => w.value === r.warehouse_id_fk)
      const enriched = Object.assign({}, r, {
        warehouse: {
          transportation_types: warehouseObj ? warehouseObj.transportation_types : []
        }
      })
      this.currentProcessOrder = enriched
      this.checkAlert = false
      this.clearProcessOrderData()
      this.theQuantity.quantity = r.quantity
      this.updateData.quantity_charge = r.quantity_charge
      // Force a real false->true transition on the child's `dialog` watcher so its reset/
      // prefill logic always re-runs. If a different order's dialog was left open (e.g. a
      // second gear icon clicked before the first was closed), `dialog` is already true and
      // setting it true again is a no-op — the child then keeps showing/validating the
      // previous order's stale updateData, which is what caused the "Int. Shipping" field to
      // show a stray required error on a now-valid value.
      if (this.$refs.processComp) {
        this.$refs.processComp.dialog = false
      }
      this.$nextTick(() => {
        if (this.$refs.processComp) {
          this.$refs.processComp.dialog = true
          // Single-instance pattern bypasses processTheOrder.vue's own gear-icon click
          // handler, which is what normally fetches these — without them exchangeBase (etc.)
          // stays 0, so editing a non-USD warehouse order's item price silently fails to
          // recalculate the total (same bug as allOrder.vue, reproduced on order TR-748).
          this.$refs.processComp.calculateTotalAmount()
          this.$refs.processComp.getExchangeRate()
          this.$refs.processComp.getAEDRate()
          this.$refs.processComp.getCNYRate()
          this.$refs.processComp.getGBPRate()
          this.$refs.processComp.getEURRate()
        }
      })
    },

    calculateTotalAmount() {
      let itemPrice = Number(this.updateData.item_price) || 0
      let shipping = Number(this.updateData.internal_shipping_handling) || 0
      let tax = Number(this.updateData.tax) || 0
      let nationalTax = Number(this.updateData.national_tax) || 0
      let delivery = Number(this.updateData.delivery_price) || 0
      let fee = Number(this.updateData.kreenex_fee) || 0
      let insurance = Number(this.updateData.insurance) || 0
      let qttCharge = Number(this.updateData.quantity_charge) || 0
      let discount = Number(this.updateData.discount) || 0
      let qtt = Number(this.theQuantity.quantity) || 0
      let repackagePrice = Number(this.updateData.repackage_price) || 0
      let holdPrice = Number(this.updateData.hold_price) || 0
      let otherServicePrice = Number(this.updateData.other_service_price) || 0
      const _userData = JSON.parse(localStorage.getItem('user_data') || '{}')
      const _exRate = Number(_userData.ex_rate) || 1
      let localDelivery = (Number(this.updateData.local_delivery) || 0) / _exRate

      // Per-unit shipping, matching newOrder.vue/editOrderCharge.vue: item price and shipping
      // are both charged per unit. Confirmed against live data — 76 of 79 existing orders with
      // qty>1 and shipping>0 match (item_price + shipping) * qty, not this flat variant.
      let total = (itemPrice + shipping) * qtt + tax + nationalTax + delivery + fee + insurance + qttCharge + localDelivery

      if (this.updateData.is_repackage) total += repackagePrice
      if (this.updateData.is_hold) total += holdPrice
      if (otherServicePrice > 0) total += otherServicePrice
      if (discount > 0) total -= discount

      this.updateData.total_amount = total.toFixed(2)
    },

    clearProcessOrderData() {
      this.updateData.item_price = null
      this.updateData.the_tl_price = null
      this.updateData.the_aed_price = null
      this.updateData.the_cny_price = null
      this.updateData.the_gbp_price = null
      this.updateData.the_eur_price = null
      this.updateData.tax = null
      this.updateData.item_weight = null
      this.updateData.internal_shipping_handling = 0
      this.updateData.delivery_price = null
      this.updateData.kreenex_fee = null
      this.updateData.insurance = null
      this.updateData.total_amount = null
      this.updateData.from_date = null
      this.updateData.to_date = null
      this.updateData.quantity_charge = null
      this.updateData.description = ''
      this.updateData.item_type = ''
      this.updateData.item_weight_lb = null
      this.updateData.discount = null
      this.updateData.send_sms = false
      this.updateData.is_repackage = false
      this.updateData.is_hold = false
      this.updateData.other_service = ''
      this.updateData.repackage_price = null
      this.updateData.hold_price = null
      this.updateData.other_service_price = null
      this.updateData.local_delivery = null
      this.updateData.tax = null
      this.updateData.national_tax = null
      this.updateData.process_note = ''
      this.updateData.customer_process_note = ''
      this.updateData.customer_id_fk = null
      this.updateData.product_name = null
      this.updateData.product_url = null
      this.updateData.quantity = null
      this.updateData.color = null
      this.updateData.size = null
      this.updateData.note = null
      this.updateData.brand = null
      this.updateData.order_image = null
      this.checkAlert = false
    },

    processOrder(event, id, stime, stime1, stime2) {
      let token = localStorage.getItem('access_token')
      this.loading = true
      this.updateData.send_sms = this.updateData.send_sms.toString()

      const formData = new FormData()

      Object.keys(this.updateData).forEach(key => {
        if (key === 'customer_process_note_image' || key === 'order_image') return
        if (this.updateData[key] !== null && this.updateData[key] !== undefined) {
          formData.append(key, this.updateData[key])
        }
      })

      if (this.updateData.customer_process_note_image instanceof File) {
        formData.append('customer_process_note_image', this.updateData.customer_process_note_image)
      }

      if (this.updateData.order_image instanceof File) {
        formData.append('order_image', this.updateData.order_image)
      }

      axios.post(this.processOrderUrl + '/' + id, formData, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(() => {
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
          this.checkAlert = true
          this.getResults()
          let b = this
          setTimeout(function () {
            b.checkAlert = false
          }, 1500)
        })
        .catch((error) => {
          const rawMsg = error && error.response && error.response.data && error.response.data.message
          const msg = rawMsg && typeof rawMsg === 'object'
            ? Object.values(rawMsg).flat().join('\n')
            : (rawMsg || this.$t('orders.errorOccurredProcessingOrder'))
          swal(this.$t('common.error'), msg, 'error')
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
        })
    },

    localItemPrice(r) {
      if (r.country === 'Turkey' && r.the_tl_price) return { label: this.$t('customer.itemPrice') + ' TL', display: `${r.the_tl_price} ₺` }
      if (r.country === 'UAE' && r.the_aed_price) return { label: this.$t('customer.itemPrice') + ' AED', display: `${r.the_aed_price} AED` }
      if (r.country === 'China' && r.the_cny_price) return { label: this.$t('customer.itemPrice') + ' CNY', display: `¥${r.the_cny_price}` }
      if (r.country === 'United Kingdom' && r.the_gbp_price) return { label: this.$t('customer.itemPrice') + ' GBP', display: `£${r.the_gbp_price}` }
      if (r.country === 'Germany' && r.the_eur_price) return { label: this.$t('customer.itemPrice') + ' EUR', display: `€${r.the_eur_price}` }
      return null
    },

    handleWarehouseFilter(val) {
      this.tableData.warehouse_id_fk = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/purchased_order'
      this.getResults()
    },

    handlePlatformFilter(val) {
      this.tableData.platform = Array.isArray(val) && val.length ? val.join(',') : ''
      this.getResults()
    },

    getReasons() {
      let token = localStorage.getItem('access_token')
      axios.get('api/reason/list', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data
          this.reasons = result.map(r => ({
            text: r.cancel_reason,
            value: r.cancel_reason,
            ar: r.cancel_reason_ar,
            ku: r.cancel_reason_ku
          }))
        })
    },
  },
}
</script>

<style>
.table-tr:hover .action-td {
  position: sticky;
  right: -2px;
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}
.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden;
  overflow-y: hidden;
}

.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

.table > thead > tr > th, .table > tbody > tr > th, .table > tfoot > tr > th, .table > thead > tr > td, .table > tbody > tr > td, .table > tfoot > tr > td
{
  padding: 0 5px !important;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}

/* Mobile order cards: see assets/mobile-order-cards.css */

.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}
.combined-cell-divider {
  width: 100%;
  border-top: 1px solid #ddd;
  margin: 3px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/purchasedOrder.vue